import { create } from "zustand";
import { persist } from "zustand/middleware";
import { Module, Chat, Message } from "@shared/schema";
import { setAuthToken as setGlobalAuthToken } from "./queryClient";

interface PendingCampaignCreative {
  url: string;
  name: string;
  prompt?: string;
  type?: "image" | "video";
}

export interface CampaignGraphicsHandoff {
  adPrompt: string;
  referenceImages: { name: string; dataUrl: string }[];
}

/** Transient handoff from Analytics "Crosspostuj" → Scheduler composer. NOT persisted. */
export interface PendingCrosspost {
  mediaUrl: string;
  mediaType: "video" | "image";
  caption: string;
  sourcePlatform: string;
}

interface AppState {
  currentModule: string;
  modules: Module[];
  chats: Chat[];
  currentChatId: string | null;
  messages: Message[];
  isLoading: boolean;
  worksheetModalOpen: boolean;
  offerSheetModalOpen: boolean;
  userCloneModalOpen: boolean;
  authToken: string | null;
  pendingCampaignCreatives: PendingCampaignCreative[];
  /** Selected VP carousel style ID (e.g. 'noir-editorial') */
  carouselStyleId: string | null;
  /** Brand-asset ID used as the profile avatar in the top-left of every slide. */
  carouselProfileAssetId: string | null;
  /** Display name rendered next to the profile avatar (e.g. "Paweł Kowalski"). */
  carouselProfileName: string;

  /** Stories Creator settings (per-session) */
  storiesFrameCount: number;
  storiesFlowId: string;
  storiesBackgroundMode: "consistent" | "varied";
  storiesImageProvider:
    | "nano-banana"
    | "nano-banana-pro"
    | "nano-banana-2"
    | "gpt-image-1"
    | "gpt-image-2";
  storiesUserBackgroundR2Key: string | null;
  storiesUserBackgroundR2Keys: string[];
  storiesUserBackgroundMode: "as-is" | "variation";
  /** Per-frame assignment: r2Key → 1-based frame index. Missing keys = auto round-robin. */
  storiesUserBackgroundAssignments: Record<string, number>;
  /** VP-230 4b: signed URL per wgrane tło (r2Key → url) — podgląd klatek pokazuje
   *  wgrane zdjęcie od razu, zanim cokolwiek zostanie wygenerowane. */
  storiesUserBackgroundPreviews: Record<string, string>;

  /** Handoff from campaign creator → ad-graphics-builder (step 4) */
  campaignGraphicsHandoff: CampaignGraphicsHandoff | null;

  /** Transient handoff from Analytics "Crosspostuj" → Scheduler composer (not persisted). */
  pendingCrosspost: PendingCrosspost | null;

  /** Global agency client selection — persisted, used across all pages */
  selectedClientId: string | null;
  selectedClientUserId: string | null;
  selectedClientName: string | null;

  /** Active tab in ContentCreator when navigated from sidebar (organic mode). */
  organicTab: string | null;

  // Actions
  setCurrentModule: (moduleId: string) => void;
  setModules: (modules: Module[]) => void;
  setChats: (chats: Chat[]) => void;
  setCurrentChatId: (chatId: string | null) => void;
  setMessages: (messages: Message[]) => void;
  addMessage: (message: Message) => void;
  setLoading: (loading: boolean) => void;
  setWorksheetModalOpen: (open: boolean) => void;
  setOfferSheetModalOpen: (open: boolean) => void;
  setUserCloneModalOpen: (open: boolean) => void;
  setAuthToken: (token: string) => void;
  clearAuth: () => void;
  setPendingCampaignCreatives: (creatives: PendingCampaignCreative[]) => void;
  clearPendingCampaignCreatives: () => void;
  setCarouselStyleId: (id: string | null) => void;
  setCarouselProfileAssetId: (id: string | null) => void;
  setCarouselProfileName: (name: string) => void;
  setStoriesFrameCount: (n: number) => void;
  setStoriesFlowId: (id: string) => void;
  setStoriesBackgroundMode: (m: "consistent" | "varied") => void;
  setStoriesImageProvider: (
    p: "nano-banana" | "nano-banana-pro" | "nano-banana-2" | "gpt-image-1" | "gpt-image-2"
  ) => void;
  setStoriesUserBackgroundR2Key: (k: string | null) => void;
  setStoriesUserBackgroundR2Keys: (keys: string[]) => void;
  setStoriesUserBackgroundMode: (m: "as-is" | "variation") => void;
  cycleStoriesUserBackgroundAssignment: (r2Key: string, maxFrames: number) => void;
  clearStoriesUserBackgroundAssignment: (r2Key: string) => void;
  addStoriesUserBackgroundR2Key: (key: string) => void;
  removeStoriesUserBackgroundR2Key: (key: string) => void;
  setStoriesUserBackgroundPreview: (r2Key: string, url: string) => void;
  setSelectedClient: (client: { id: string; userId: string | null; name: string } | null) => void;
  setOrganicTab: (tab: string | null) => void;
  setCampaignGraphicsHandoff: (handoff: CampaignGraphicsHandoff | null) => void;
  setPendingCrosspost: (v: PendingCrosspost | null) => void;
}

export const useStore = create<AppState>()(
  persist(
    (set) => ({
      currentModule: "home",
      modules: [],
      chats: [],
      currentChatId: null,
      messages: [],
      isLoading: false,
      worksheetModalOpen: false,
      offerSheetModalOpen: false,
      userCloneModalOpen: false,
      authToken: null,
      pendingCampaignCreatives: [],
      carouselStyleId: null,
      carouselProfileAssetId: null,
      carouselProfileName: "",
      storiesFrameCount: 5,
      storiesFlowId: "hook-truth-pivot-cta",
      storiesBackgroundMode: "consistent",
      storiesImageProvider: "nano-banana-pro",
      storiesUserBackgroundR2Key: null,
      storiesUserBackgroundR2Keys: [],
      storiesUserBackgroundMode: "as-is",
      storiesUserBackgroundAssignments: {},
      storiesUserBackgroundPreviews: {},
      campaignGraphicsHandoff: null,
      pendingCrosspost: null,
      selectedClientId: null,
      selectedClientUserId: null,
      selectedClientName: null,
      organicTab: null,

      setCurrentModule: (moduleId) => set({ currentModule: moduleId }),
      setModules: (modules) => set({ modules }),
      setChats: (chats) => set({ chats }),
      setCurrentChatId: (chatId) => set({ currentChatId: chatId }),
      setMessages: (messages) => set({ messages }),
      addMessage: (message) =>
        set((state) => ({
          messages: [...state.messages, message],
        })),
      setLoading: (loading) => set({ isLoading: loading }),
      setWorksheetModalOpen: (open) => set({ worksheetModalOpen: open }),
      setOfferSheetModalOpen: (open) => set({ offerSheetModalOpen: open }),
      setUserCloneModalOpen: (open) => set({ userCloneModalOpen: open }),
      setAuthToken: (token) => {
        setGlobalAuthToken(token);
        set({ authToken: token });
      },
      clearAuth: () => {
        console.warn("clearAuth() called - clearing all chat data", {
          timestamp: new Date().toISOString(),
        });
        setGlobalAuthToken(null);
        // Tie the impersonation-origin marker to the token lifecycle so a stale
        // marker can never mis-route a later session.
        localStorage.removeItem("gawronify-impersonation-origin");
        set({ authToken: null, currentChatId: null, chats: [], messages: [] });
      },
      setPendingCampaignCreatives: (creatives) => set({ pendingCampaignCreatives: creatives }),
      clearPendingCampaignCreatives: () => set({ pendingCampaignCreatives: [] }),
      setCarouselStyleId: (id) => set({ carouselStyleId: id }),
      setCarouselProfileAssetId: (id) => set({ carouselProfileAssetId: id }),
      setCarouselProfileName: (name) => set({ carouselProfileName: name }),
      setStoriesFrameCount: (n) => set({ storiesFrameCount: n }),
      setStoriesFlowId: (id) => set({ storiesFlowId: id }),
      setStoriesBackgroundMode: (m) => set({ storiesBackgroundMode: m }),
      setStoriesImageProvider: (p) => set({ storiesImageProvider: p }),
      setStoriesUserBackgroundR2Key: (k) => set({ storiesUserBackgroundR2Key: k }),
      setStoriesUserBackgroundR2Keys: (keys) => set({ storiesUserBackgroundR2Keys: keys }),
      setStoriesUserBackgroundMode: (m) => set({ storiesUserBackgroundMode: m }),
      addStoriesUserBackgroundR2Key: (key) =>
        set((state) => ({
          storiesUserBackgroundR2Keys: state.storiesUserBackgroundR2Keys.includes(key)
            ? state.storiesUserBackgroundR2Keys
            : [...state.storiesUserBackgroundR2Keys, key],
        })),
      removeStoriesUserBackgroundR2Key: (key) =>
        set((state) => {
          const rest = { ...state.storiesUserBackgroundAssignments };
          delete rest[key];
          const restPreviews = { ...state.storiesUserBackgroundPreviews };
          delete restPreviews[key];
          return {
            storiesUserBackgroundR2Keys: state.storiesUserBackgroundR2Keys.filter((k) => k !== key),
            storiesUserBackgroundAssignments: rest,
            storiesUserBackgroundPreviews: restPreviews,
          };
        }),
      setStoriesUserBackgroundPreview: (r2Key, url) =>
        set((state) => ({
          storiesUserBackgroundPreviews: { ...state.storiesUserBackgroundPreviews, [r2Key]: url },
        })),
      cycleStoriesUserBackgroundAssignment: (r2Key, maxFrames) =>
        set((state) => {
          const current = state.storiesUserBackgroundAssignments[r2Key];
          // auto (undefined) → 1 → 2 → … → maxFrames → auto
          const next = current === undefined ? 1 : current >= maxFrames ? undefined : current + 1;
          const updated = { ...state.storiesUserBackgroundAssignments };
          if (next === undefined) delete updated[r2Key];
          else updated[r2Key] = next;
          return { storiesUserBackgroundAssignments: updated };
        }),
      clearStoriesUserBackgroundAssignment: (r2Key) =>
        set((state) => {
          const rest = { ...state.storiesUserBackgroundAssignments };
          delete rest[r2Key];
          return { storiesUserBackgroundAssignments: rest };
        }),
      setSelectedClient: (client) =>
        set({
          selectedClientId: client?.id ?? null,
          selectedClientUserId: client?.userId ?? null,
          selectedClientName: client?.name ?? null,
        }),
      setCampaignGraphicsHandoff: (handoff) => set({ campaignGraphicsHandoff: handoff }),
      setPendingCrosspost: (v) => set({ pendingCrosspost: v }),
      setOrganicTab: (tab) => set({ organicTab: tab }),
    }),
    {
      name: "gawronify-storage",
      partialize: (state) => ({
        authToken: state.authToken,
        selectedClientId: state.selectedClientId,
        selectedClientUserId: state.selectedClientUserId,
        selectedClientName: state.selectedClientName,
        organicTab: state.organicTab,
      }),
      onRehydrateStorage: () => (state) => {
        // Sync global auth token when persisted state is restored from localStorage
        if (state?.authToken) {
          setGlobalAuthToken(state.authToken);
        }
      },
    }
  )
);
